import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '@env/environment';
import {
  AncillariesInt,
  BoardingPassInt,
  ExtrasBagsAncillarieInt,
  InfoApiInt,
  PNRLookupInt,
} from '@interfaces/';
import { catchError, map } from 'rxjs/operators';
import { Reservation } from '@app/models/reservation.model';
import { PnrLookUpMapper } from '@app/mappers';
import { PassengerMapper } from '@app/mappers/passenger.mapper';
import { CheckInResultInt } from '@app/interfaces/passenger.interface';
import { AncillariesMapper } from '@app/mappers/ancillaries.mapper';
import { GlobalsService } from './globals.service';
import { Passenger } from '@app/models';
import { CartMapper } from '@app/mappers/cart.mapper';
import { ResponsePNRUdateInt } from '@app/interfaces/responsePNRUpdate.interface';
import { LoadingService } from './loading.service';
import { AdminService } from '@services/admin.service';
import { TypeLoading } from '@app/enums';
import { ErrorAdapter } from '@models/adapter/error.adapter';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  url = `${environment.urlApi}`;

  constructor(
    private http: HttpClient,
    private _globalService: GlobalsService,
    private _loadingService: LoadingService,
    private _admin: AdminService
  ) {}

  lookupPNR(recordLocator: string, lastName?: string): Observable<Reservation[]> {
    const fromObject: Record<string, string> = { recordLocator };
    if (lastName) {
      fromObject['lastName'] = lastName;
    }
    const params = new HttpParams({ fromObject });
    const { Location } = this._admin.admin;
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.get<PNRLookupInt>(`${this.url}/pnr`, { params }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this._globalService.setPNRLookUpApi(res?._collection[0]);
        return PnrLookUpMapper.pnrBuild(res?._collection[0], Location);
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  lookupPNRByDestination(parameters: {
    from: string;
    to?: string;
    fname: string;
    lname: string;
  }): Observable<Reservation[]> {
    const params = new HttpParams({ fromObject: parameters });
    const { Location } = this._admin.admin;
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.get<PNRLookupInt>(`${this.url}/pnr`, { params }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this._globalService.setPNRLookUpApi(res?._collection[0]);
        return PnrLookUpMapper.pnrBuild(res?._collection[0], Location);
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  lookupReservationByFlightNumber(parameters: {
    flight: string;
    lname: string;
    fname: string;
    from: string;
    premiereUpgrade: string;
    volunteerOffer: string;
  }) {
    const params = new HttpParams({ fromObject: parameters });
    const { Location } = this._admin.admin;
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.get<PNRLookupInt>(`${this.url}/pnr`, { params }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        this._globalService.setPNRLookUpApi(res?._collection[0]);
        return PnrLookUpMapper.pnrBuild(res?._collection[0], Location);
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  reprintBoardingPasses(parameters: {
    pnrCode: string;
    language: string;
    legCode: string;
    passengerIds: string[];
  }): Observable<any> {
    const { pnrCode, language, legCode, passengerIds } = parameters;
    let paxId = '';
    let paramsConcat = '';
    const params = new HttpParams({
      fromObject: { language: 'ES', legCode },
    });
    for (let id of passengerIds) {
      paxId += `&paxId=${id}`;
    }
    paramsConcat = params.toString() + paxId;
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.get<any>(`${this.url}/boarding-passes/${pnrCode}?${paramsConcat}`).pipe(
      map((res: BoardingPassInt) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return PnrLookUpMapper.mapBoardinPass(res);
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  checkin(
    cartId: string,
    cardInfo?: any,
    paymentAmount: any = null,
    currency?: string,
    authResponse: any = null
  ): Observable<CheckInResultInt> {
    const timeOfRequest = new Date().getTime().toString();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'MX',
      overrideTsa: 'true',
      overrideDocv: 'true',
      kioskId: 'ATOMEXK01',
      uiVersion: '4.5.19-109',
      languageUI: 'es_MX',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
    };
    const body: any = {
      _meta: {
        class: 'PurchaseOrder',
      },
      cartId,
      operation: 'CHECKIN',
      paymentInfo: paymentAmount,
      authorizationResult: authResponse,
    };
    const params = new HttpParams({ fromObject: p });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.post<any>(`${this.url}/po`, body, { params }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return new PassengerMapper().checkIn(res, this._globalService.getPNRLookup());
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  getAncillaries(cartId): Observable<ExtrasBagsAncillarieInt> {
    const timeOfRequest = new Date().getTime().toString();
    const reservation = this._globalService.getReservation();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'mx',
      overrideTsa: 'true',
      overrideDocv: 'true',
      uiVersion: 'E1.6.3-956',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
      languageUI: 'es_MX',
      cartId,
    };
    const params = new HttpParams({ fromObject: p });
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.get<AncillariesInt>(`${this.url}/ancillaries`, { params }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        const ancillaries = AncillariesMapper.getAncillaries(res, reservation);
        this._globalService.setAncillaries(ancillaries);

        return ancillaries;
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  updateCartInfo(reservation: Reservation, passengers: Passenger[]) {
    const cartId = reservation?.apiCart?.cartId;
    const timeOfRequest = new Date().getTime().toString();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'mx',
      overrideTsa: 'true',
      overrideDocv: 'true',
      uiVersion: 'E1.6.3-956',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
      languageUI: 'es_MX',
      cartId,
    };
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http
      .put(
        `${this.url}/carts/${cartId}`,
        CartMapper.mapPasengerToRequest(reservation, passengers),
        { params: p }
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError(this.handlerError.bind(this))
      );
  }

  updateCartInfoSeat(reservation: Reservation, data) {
    const cartId = reservation?.apiCart?.cartId;
    const timeOfRequest = new Date().getTime().toString();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'mx',
      overrideTsa: 'true',
      overrideDocv: 'true',
      uiVersion: 'E1.6.3-956',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
      languageUI: 'es_MX',
      cartId,
    };
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.put(`${this.url}/carts/${cartId}`, data, { params: p }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return res;
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  updateCartPax(passenger: Passenger, reservation: Reservation) {
    const cartId = reservation.flightSummary.cartId;
    const postData = passenger?.internationFlow.getRequestCartPnrUpdate(passenger, reservation);
    const timeOfRequest = new Date().getTime().toString();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'mx',
      overrideTsa: 'true',
      overrideDocv: 'true',
      uiVersion: 'E1.6.3-956',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
      languageUI: 'es_MX',
      cartId,
    };
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.put(`${this.url}/carts/${cartId}`, postData, { params: p }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return res;
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  updateFrequentFlyerNumber(passenger: Passenger, cartId) {
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http
      .put<ResponsePNRUdateInt>(
        `${this.url}/carts/${cartId}`,
        CartMapper.mapPasengerToRequestFrequentFly(passenger)
      )
      .pipe(
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          return res;
        }),
        catchError(this.handlerError.bind(this))
      );
  }

  getApiInfo(): Observable<InfoApiInt> {
    return this.http.get<InfoApiInt>(`${this.url}/`).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return res;
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  handlerError(error: HttpErrorResponse) {
    this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
    const errorAdapt = ErrorAdapter.adapt(error?.error);
    return throwError(errorAdapt);
  }

  updateGender(passengers: Passenger[], cartId) {
    const body = CartMapper.mapPasengerToRequestGender(passengers);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this.http.put<ResponsePNRUdateInt>(`${this.url}/carts/${cartId}`, body).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return res;
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  usperson(usPerson, cartId) {
    var data = {
      passengers: usPerson,
    };
    const timeOfRequest = new Date().getTime().toString();
    const p = {
      timeOfRequest,
      pos: 'kiosk',
      store: 'MX',
      overrideTsa: 'true',
      overrideDocv: 'true',
      kioskId: 'ATOMEXK01',
      uiVersion: '4.5.19-109',
      languageUI: 'es_MX',
      location: 'MEX',
      currency: 'MXN',
      paymentEnabled: 'true',
      cartId: cartId,
    };
    const params = new HttpParams({ fromObject: p });
    return this.http.post<any>(`${this.url}/usperson/`, data, { params }).pipe(
      map((res) => {
        data;
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return res;
      }),
      catchError(this.handlerError.bind(this))
    );
  }
}
